import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { 
  X, Crown, Sparkles, Lock, Check, Gift, Zap, Star, 
  BookOpen, Heart, Trophy, ChevronRight, Percent
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Journey Unlock Prompt - Shows when users earn discounts or reach milestones
const JourneyUnlockPrompt = ({ prompt, onClose, onAction }) => {
  if (!prompt) return null;

  const getIcon = () => {
    switch (prompt.type) {
      case "discount": return <Percent className="h-6 w-6" />;
      case "encourage": return <Sparkles className="h-6 w-6" />;
      default: return <Crown className="h-6 w-6" />;
    }
  };

  const getGradient = () => {
    switch (prompt.tier) {
      case "essence": return "from-emerald-500 to-teal-500";
      case "awakening": return "from-violet-500 to-purple-500";
      case "transcendence": return "from-amber-500 to-orange-500";
      default: return "from-purple-500 to-pink-500";
    }
  };

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-40 animate-slide-up">
      <Card className={`bg-gradient-to-r ${getGradient()} text-white border-0 shadow-2xl overflow-hidden`}>
        <CardContent className="p-4 relative">
          <button 
            onClick={onClose}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg mb-1">{prompt.title}</h3>
              <p className="text-sm text-white/90 mb-3">{prompt.message}</p>
              {prompt.urgency && (
                <p className="text-xs text-white/70 mb-2 flex items-center gap-1">
                  <Zap className="h-3 w-3" />
                  {prompt.urgency}
                </p>
              )}
              <Link to="/subscription" onClick={onClose}>
                <Button 
                  size="sm"
                  className="bg-white text-gray-900 hover:bg-white/90 rounded-full"
                >
                  {prompt.cta}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Journey Progress Card - Shows milestones and progress
const JourneyProgressCard = ({ progress, compact = false }) => {
  if (!progress) return null;

  const { milestones, current_level, total_points, next_milestone, completed_milestones, total_milestones } = progress;

  if (compact) {
    return (
      <Link to="/wellness-journey">
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-500/10 dark:to-purple-500/10 border-violet-200/50 dark:border-violet-500/30 hover:shadow-md transition-all cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-violet-500" />
                <span className="text-xs font-medium text-violet-600">Journey Progress</span>
              </div>
              <span className="text-xs text-violet-500">{completed_milestones}/{total_milestones}</span>
            </div>
            <Progress value={(completed_milestones / total_milestones) * 100} className="h-2 mb-2" />
            {next_milestone && (
              <p className="text-xs text-muted-foreground">
                Next: {next_milestone.name} ({next_milestone.progress}%)
              </p>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-primary flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Your Journey
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Level {current_level?.level}</span>
            <span className="text-sm font-medium text-primary">{current_level?.name}</span>
          </div>
        </div>

        {/* Milestones */}
        <div className="space-y-3 mb-4">
          {milestones?.slice(0, 4).map((milestone) => (
            <div key={milestone.id} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                milestone.completed 
                  ? 'bg-emerald-500/20 text-emerald-500' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {milestone.completed ? <Check className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${milestone.completed ? 'text-primary' : 'text-muted-foreground'}`}>
                    {milestone.name}
                  </span>
                  <span className="text-xs text-muted-foreground">{milestone.progress}%</span>
                </div>
                <Progress value={milestone.progress} className="h-1 mt-1" />
              </div>
            </div>
          ))}
        </div>

        {/* Total Points */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-sm text-muted-foreground">Total Points</span>
          <span className="font-bold text-primary flex items-center gap-1">
            <Star className="h-4 w-4 text-amber-500" />
            {total_points}
          </span>
        </div>

        <Link to="/wellness-journey" className="block mt-4">
          <Button variant="outline" className="w-full rounded-full">
            View Full Journey
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

// Discount Badge - Shows earned discounts
const DiscountBadge = ({ discount }) => {
  if (!discount) return null;

  const tierColors = {
    essence: "bg-emerald-500",
    awakening: "bg-violet-500",
    transcendence: "bg-amber-500"
  };

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-xs font-medium ${tierColors[discount.tier]}`}>
      <Gift className="h-3 w-3" />
      {discount.discount_percent}% OFF
    </div>
  );
};

// Hook to fetch and manage journey progress
export const useJourneyProgress = () => {
  const { user, getAuthHeader } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePrompt, setActivePrompt] = useState(null);

  const fetchProgress = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/api/journey/progress`, {
        headers: getAuthHeader()
      });
      setProgress(response.data);
      
      // Show prompt if user has earned new rewards and isn't premium
      if (!response.data.is_premium && response.data.prompts?.length > 0) {
        // Check if we've shown this prompt recently
        const lastPromptTime = localStorage.getItem('lastJourneyPrompt');
        const now = Date.now();
        const hourAgo = now - (60 * 60 * 1000);
        
        if (!lastPromptTime || parseInt(lastPromptTime) < hourAgo) {
          setActivePrompt(response.data.prompts[0]);
          localStorage.setItem('lastJourneyPrompt', now.toString());
        }
      }
    } catch (error) {
      console.error("Failed to fetch journey progress:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, [user]);

  const dismissPrompt = () => {
    setActivePrompt(null);
  };

  const trackAffirmationView = async () => {
    if (!user) return;
    try {
      await axios.post(`${API}/api/journey/track-view`, {}, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  return {
    progress,
    loading,
    activePrompt,
    dismissPrompt,
    trackAffirmationView,
    refetch: fetchProgress
  };
};

export { JourneyUnlockPrompt, JourneyProgressCard, DiscountBadge };
export default JourneyProgressCard;
